import { Fragment, ReactNode, SyntheticEvent, useEffect, useState } from 'react'

import { useDebounce } from '@img-arena/consoles-utils'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import SearchIcon from '@mui/icons-material/Search'
import {
  Autocomplete as MuiAutocomplete,
  CircularProgress,
  MenuItem,
  Stack,
  Typography
} from '@mui/material'
import { dialogInputStopPropagation } from '@/components/ConsoleDialog'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'
import { warnDev } from '@/utils/warnDev'

import { ConsoleTextField } from '../StyledInputComponents'
import {
  AUTOCOMPLETE_TEST_IDS,
  LOAD_MORE_DATA_SCROLL_OFFSET,
  OPTIONS_RESET_UPPER_LIMIT
} from './Autocomplete.constants'
import { AutocompleteProps } from './Autocomplete.types'
import { EndInputAdornment } from './EndInputAdornment'

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const Autocomplete = <Option extends unknown>({
  label,
  loading = false,
  multiselect = false,
  pagination,
  testId = AUTOCOMPLETE_TEST_IDS.root,
  defaultValue,
  error,
  excludeSelected = true,
  maxListHeight = 360,
  noOptionsText = 'No options found',
  charThresholdToOpen = 0,
  clearOnSelect = false,
  limitTags,
  groupBy,
  onSearch,
  required,
  placement = 'bottom-start',
  dataError,
  listBoxWidth,
  width,
  fullWidth = true,
  openOnFocus = true,
  blurOnSelect = true,
  clearOnBlur = true,
  disabled = false,
  isOptionEqualToValue,
  clearOnDisable,
  disabledInfo,
  value,
  renderValue: _renderValue,
  inputValue: externalInputValue,
  onInputChange: externalOnInputChange,
  ...props
}: AutocompleteProps<Option>) => {
  const [hasLoaded, setHasLoaded] = useState(false)
  const [selected, setSelected] = useState<Option | Option[] | null>(
    multiselect ? defaultValue || [] : defaultValue || null
  )

  const selectedValue = value !== undefined ? value : selected

  const [ft, setFT] = useState('')

  const inputValue = externalInputValue !== undefined ? externalInputValue : ft
  const setInputValue =
    externalOnInputChange ||
    ((event: React.SyntheticEvent, value: string) => {
      if (value === '' && ft && ft.length > 0 && event?.type !== 'change') {
        return
      }

      setFT(value)
    })
  const debouncedSearch = useDebounce(inputValue, 250)
  const numberOfSelectedOptions = multiselect
    ? (selectedValue as Option[])?.length
    : selectedValue
      ? 1
      : 0

  const { page, total, loadingMore } = pagination || {}
  const baseOptions = inputValue.length < charThresholdToOpen ? [] : props.options || []

  const options = loadingMore
    ? [...baseOptions, { __loading__: true, label: 'Loading...' } as Option]
    : baseOptions

  const isMoreData = !total ? true : total > baseOptions.length

  useEffect(() => {
    if (debouncedSearch.length >= charThresholdToOpen && hasLoaded) {
      loadMoreOptions(0, debouncedSearch)
      onSearch?.(debouncedSearch)
    }
  }, [debouncedSearch])

  useEffect(() => {
    if (charThresholdToOpen !== 0) {
      setHasLoaded(true)
    }

    const setLoadedTimer = setTimeout(() => {
      setHasLoaded(true)
    }, 50)

    return () => {
      clearTimeout(setLoadedTimer)
    }
  }, [])

  useEffect(() => {
    if (disabled && clearOnDisable) {
      onClear()
    }
  }, [disabled])

  const getNoOptionsText = (treshold: number, inputLegth: number) => {
    const diff = treshold - inputLegth
    const pluralStr = diff !== 1 ? 's' : ''

    if (diff <= 0) return noOptionsText

    return `Type ${diff} more character${pluralStr} to search`
  }

  const filterOptions = (options: Option[], state: { inputValue: string }) => {
    if (props?.filterOptions) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return props.filterOptions(options, { ...state, selected: selectedValue } as any)
    }

    return options
  }

  const getOptionLabel = (option: Option) => {
    if ((option as Record<string, unknown>).__loading__) {
      return 'Loading...'
    }

    if (props?.getOptionLabel) {
      return props.getOptionLabel(option)
    }

    if (typeof option !== 'string' || typeof option !== 'number') {
      warnDev(
        'Option should be a string or number, or component should provide getOptionLabel prop'
      )
    }

    return `${option}`
  }

  const loadMoreOptions = (page = 0, _ft?: string) => {
    if (loading) return
    if (page && page > 0 && !isMoreData) return
    if (debouncedSearch.length < charThresholdToOpen || inputValue.length < charThresholdToOpen) {
      return
    }

    props?.loadMoreOptions?.(page, _ft)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderOption = ({ key, ...rest }: any, option: Option, state: { selected: boolean }) => {
    const extendedProps = { ...rest, role: 'option' }

    if ((option as Record<string, unknown>).__loading__) {
      return (
        <MenuItem key="loading-indicator" disabled>
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            width="100%"
            padding={0.5}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Loading more...
            </Typography>
          </Stack>
        </MenuItem>
      )
    }

    return (
      <Fragment key={`${key}-${extendedProps['data-option-index']}`}>
        {props?.renderOption?.(extendedProps, option, state) || (
          <MenuItem {...extendedProps}>
            <Typography>{getOptionLabel(option)}</Typography>
          </MenuItem>
        )}
      </Fragment>
    )
  }

  const resetOptions = () => {
    const isBelowCharThreshold = debouncedSearch.length < charThresholdToOpen
    const isAboveOptionLimit = options.length > OPTIONS_RESET_UPPER_LIMIT

    if ((clearOnBlur && isBelowCharThreshold) || isAboveOptionLimit) {
      props?.resetOptions?.()
    }
  }

  const onClear = () => {
    const blankOption = multiselect ? ([] as Option[]) : null

    resetOptions()
    setFT('')
    setSelected(blankOption)
    onChange?.(blankOption)
    props?.onClear?.()
  }

  const onChange = (newValue: Option[] | Option | null) => {
    if (!clearOnSelect) {
      setSelected(newValue)
    }

    props?.onChange?.(newValue as Option & Option[])
  }

  const onClose = (e: SyntheticEvent) => {
    if (externalOnInputChange) {
      externalOnInputChange(e, '')
    } else {
      resetOptions()
      setFT('')
    }
    props?.onClose?.()
  }

  const _isOptionEqualToValue = (option: Option, value: Option) => {
    if (
      (option as Record<string, unknown>).__loading__ ||
      (value as Record<string, unknown>).__loading__
    ) {
      return false
    }

    if (isOptionEqualToValue) {
      return isOptionEqualToValue(option, value)
    }

    if (typeof option !== 'string' || typeof option !== 'number') {
      warnDev(
        'Option should be a string or number, or component should provide isOptionEqualToValue prop'
      )
    }

    return option === value
  }

  const renderValue = _renderValue
    ? (_renderValue as (selected: NoInfer<Option> | NoInfer<Option>[]) => ReactNode)
    : undefined

  return (
    <MuiAutocomplete
      {...dialogInputStopPropagation}
      size="small"
      fullWidth={fullWidth}
      limitTags={limitTags}
      filterOptions={filterOptions}
      renderOption={renderOption}
      isOptionEqualToValue={_isOptionEqualToValue}
      options={options}
      onOpen={() => charThresholdToOpen === 0 && !options.length && loadMoreOptions(0)}
      value={selectedValue}
      multiple={multiselect}
      disabled={disabled}
      inputValue={inputValue}
      onInputChange={(event, value) => setInputValue(event, value)}
      noOptionsText={getNoOptionsText(charThresholdToOpen, inputValue.length)}
      loading={loading}
      openOnFocus={openOnFocus}
      forcePopupIcon={false}
      onClose={onClose}
      onBlur={(e) => clearOnBlur && onClose(e)}
      clearOnBlur={clearOnBlur}
      onChange={(_, newValue) => onChange(newValue)}
      slotProps={{
        popper: {
          placement,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...({ 'data-testid': AUTOCOMPLETE_TEST_IDS.popper } as any),
          sx: { minWidth: listBoxWidth }
        },
        listbox: {
          onScroll: (e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget

            if (scrollHeight - scrollTop <= clientHeight + LOAD_MORE_DATA_SCROLL_OFFSET) {
              loadMoreOptions((page ?? 0) + 1, debouncedSearch)
            }
          },
          sx: { maxHeight: maxListHeight },
          role: 'listbox',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...({ 'data-testid': AUTOCOMPLETE_TEST_IDS.listbox } as any)
        }
      }}
      groupBy={groupBy}
      disableCloseOnSelect={multiselect}
      disableClearable={multiselect}
      blurOnSelect={blurOnSelect}
      data-testid={testId}
      filterSelectedOptions={excludeSelected}
      getOptionLabel={getOptionLabel}
      renderValue={renderValue}
      renderInput={(params) => (
        <ConsoleTextField
          {...params}
          label={label}
          required={required}
          error={error || dataError}
          sx={{
            minHeight: 37,
            height: 'auto'
          }}
          disabled={disabled}
          testId={AUTOCOMPLETE_TEST_IDS.input}
          slotProps={{
            input: {
              ...params.InputProps,
              style: {
                paddingBottom: multiselect && numberOfSelectedOptions > 0 ? 4.375 : 5.8,
                paddingTop: multiselect && numberOfSelectedOptions > 0 ? 4.375 : 5.8,
                width
              },
              endAdornment:
                disabled && !!disabledInfo ? (
                  <EndInputAdornment
                    iconSx={{ mx: 0 }}
                    icon={
                      <ConsoleTooltip title={disabledInfo}>
                        <InfoIcon sx={{ opacity: 0.7 }} />
                      </ConsoleTooltip>
                    }
                  />
                ) : (
                  <EndInputAdornment
                    isLoading={!clearOnBlur && loading && pagination?.page === 0}
                    error={dataError}
                    icon={
                      !disabled && (
                        <SearchIcon
                          sx={{
                            color: 'inherit',
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-47%)'
                          }}
                        />
                      )
                    }
                    onClear={() => onClear()}
                    closeSx={{ my: -1 }}
                    showClearIcon={!multiselect && !!selectedValue && !disabled}
                  />
                )
            }
          }}
        />
      )}
    />
  )
}
